/**
 * Media Library tabs, shared by the Media Library page and every media picker (Ad Studio,
 * campaign chat + canvas). Same groups as the backend's MediaLibraryService.MEDIA_GROUPS
 * (GET /media?group=…, which also returns group_counts).
 */
import React from 'react';

export type MediaGroup = 'all' | 'uploads' | 'ai' | 'ads';

export const MEDIA_GROUP_TABS: { value: MediaGroup; label: string; hint: string }[] = [
  { value: 'all', label: 'All', hint: 'Everything in your library' },
  { value: 'uploads', label: 'My uploads', hint: 'Photos and logos you uploaded or imported from a link' },
  { value: 'ai', label: 'AI images', hint: 'Photos AI made for your campaigns and Ad Studio' },
  { value: 'ads', label: 'Finished ads', hint: 'Exported Ad Studio creatives and text-overlay versions' },
];

const SOURCE_LABELS: Record<string, { short: string; long: string }> = {
  upload: { short: 'Upload', long: 'Uploaded' },
  url_import: { short: 'Imported', long: 'Imported from a link' },
  ai_generated: { short: 'AI', long: 'AI image' },
  vibe_template: { short: 'Ad', long: 'Finished ad' },
  text_overlay: { short: 'Text', long: 'Text overlay' },
};

export const sourceLabel = (source: string, long = false): string => {
  const l = SOURCE_LABELS[source];
  return l ? (long ? l.long : l.short) : 'Media';
};

interface MediaGroupTabsProps {
  value: MediaGroup;
  onChange: (group: MediaGroup) => void;
  counts?: Partial<Record<MediaGroup, number>> | null;
  className?: string;
}

/** Filter pills with per-tab counts (media picker board): navy when on. */
export const MediaGroupTabs: React.FC<MediaGroupTabsProps> = ({ value, onChange, counts, className = '' }) => (
  <div role="tablist" className={`flex flex-wrap gap-1.5 ${className}`}>
    {MEDIA_GROUP_TABS.map(tab => {
      const active = tab.value === value;
      const count = counts?.[tab.value];
      return (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={active}
          title={tab.hint}
          onClick={() => onChange(tab.value)}
          className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-3.5 text-[13px] font-bold transition-colors ${
            active ? 'bg-navy text-on-navy' : 'bg-inset text-ink hover:bg-line'
          }`}
        >
          {tab.label}
          {count != null && <span className={`font-money text-[11px] ${active ? 'text-on-navy-2' : 'text-ink-3'}`}>{count}</span>}
        </button>
      );
    })}
  </div>
);
